/**
 * Encounter Print Note helpers.
 * UI-only: does not write Note, NoteVersion, Encounter, or Patient records.
 */
import { noteAuthorLabel, noteDateValue, noteStatusLabel } from "@/lib/patientChartClinical";
import { noteDisplayStatus } from "@/lib/noteApi";
import { applyGenericReportTemplate } from "@/lib/noteReportShell";
import {
  NA,
  encounterDate,
  encounterNumber,
  formatDate,
} from "@/lib/patientChart";

function printAuthor(note, encounter, currentUser) {
  const labeled = noteAuthorLabel(note, encounter, currentUser);
  if (labeled && labeled !== NA) return labeled;
  const signedBy = note?.currentVersion?.signedBy;
  if (signedBy) return `Signed by ${String(signedBy).slice(0, 8)}`;
  const original = note?.originalAuthorId;
  if (original) return String(original).slice(0, 8);
  return "";
}

export function selectedNoteContent(note, liveText) {
  if (typeof liveText === "string") return liveText;
  return String(note?.currentVersion?.content?.text || "");
}

export function buildNotePrintDocument({
  note,
  fields = null,
  encounter = null,
  currentUser = null,
  contentText,
  reportTemplate = null,
  reportContext = null,
} = {}) {
  if (!note?.id) return null;
  const status = noteDisplayStatus(note);
  const name = fields?.name && fields.name !== NA ? fields.name : "";
  const content = selectedNoteContent(note, contentText);
  const reportShell = applyGenericReportTemplate({
    template: reportTemplate,
    fields,
    encounter,
    note,
    content,
    reportContext: reportContext || {},
  });
  return {
    noteId: note.id,
    patientName: name,
    dob: fields?.birthDate ? formatDate(fields.birthDate) : "",
    mrn: fields?.mrn || "",
    encounterNumber: encounter ? encounterNumber(encounter) : "",
    dateOfService: encounter ? formatDate(encounterDate(encounter)) : "",
    noteType: note.noteType?.displayName || "Note",
    title: note.title ? String(note.title) : "",
    author: printAuthor(note, encounter, currentUser),
    status,
    statusLabel: noteStatusLabel(status),
    noteDate: formatDate(noteDateValue(note), "MMM d, yyyy h:mm a"),
    content,
    reportShell,
  };
}

export function notePrintWindowTitle(doc) {
  if (!doc) return "Clinical Note";
  const who = doc.patientName ? ` — ${doc.patientName}` : "";
  return `${doc.noteType}${who}`;
}

let printHost = null;
let printNext = null;

function hoistNotePrintRoot() {
  const root = document.querySelector(".note-print-root");
  if (!root || root.parentElement === document.body) return;
  printHost = root.parentNode;
  printNext = root.nextSibling;
  document.body.appendChild(root);
}

function restoreNotePrintRoot() {
  const root = document.querySelector(".note-print-root");
  if (!printHost || !root) {
    printHost = null;
    printNext = null;
    return;
  }
  printHost.insertBefore(root, printNext);
  printHost = null;
  printNext = null;
}

/** Move the print article to document.body for the duration of window.print. */
export function installNotePrintIsolation() {
  if (typeof window === "undefined" || window.__emrbymdNotePrintIsolation) return;
  window.__emrbymdNotePrintIsolation = true;
  window.addEventListener("beforeprint", hoistNotePrintRoot);
  window.addEventListener("afterprint", restoreNotePrintRoot);
}

/** Opens the browser print dialog. Does not change Note status. */
export function printNote(doc) {
  if (typeof window === "undefined" || typeof window.print !== "function") return;
  if (!doc?.noteId) return;
  installNotePrintIsolation();
  const previous = document.title;
  document.title = notePrintWindowTitle(doc);
  hoistNotePrintRoot();
  try {
    window.print();
  } finally {
    restoreNotePrintRoot();
    document.title = previous;
  }
}
